import { useEffect, useState } from 'react';
import { Skeleton } from '@mui/material';
import { toast } from 'react-toastify';
import { getList } from '../common/BackendProvider';
import { Panel } from '../layout/Panel';

const fallbackGradients = [
    'from-emerald-500 to-blue-500',
    'from-slate-900 to-emerald-600',
    'from-blue-600 to-cyan-400',
    'from-emerald-700 to-lime-400',
];

const formatCourseDate = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

const CourseVisual = ({ course, index }) => {
    if (course.image) {
        return (
            <img
                src={course.image}
                alt={course.image_alt_text || ''}
                className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            />
        );
    }

    return (
        <div className={`h-full w-full bg-gradient-to-br ${fallbackGradients[index % fallbackGradients.length]}`} />
    );
};

const CourseCard = ({ course, index }) => {
    const courseType = course.course_type || course.document_type || 'Course';

    return (
        <article className="group flex min-h-[430px] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
            <div className="relative h-44 overflow-hidden bg-slate-100">
                <CourseVisual course={course} index={index} />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/75 via-slate-950/35 to-transparent p-4">
                    <div className="flex max-w-full flex-wrap items-center gap-2 text-[11px] font-bold uppercase leading-none tracking-[0.08em] text-white/90">
                        {formatCourseDate(course.date || course.published_at) && (
                            <span className="rounded-full bg-slate-950/35 px-2.5 py-1 backdrop-blur-sm">
                                {formatCourseDate(course.date || course.published_at)}
                            </span>
                        )}
                        <span className="max-w-[12rem] truncate rounded-full bg-white/20 px-2.5 py-1 backdrop-blur-sm">
                            {courseType}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex flex-1 flex-col p-5">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                        {courseType}
                    </span>
                    {course.author && (
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                            {course.author}
                        </span>
                    )}
                </div>

                <h3 className="line-clamp-2 text-lg font-semibold leading-7 text-slate-950 group-hover:text-emerald-700">
                    {course.title}
                </h3>
                <p className="mt-3 line-clamp-4 text-sm leading-6 text-slate-600">
                    {course.excerpt || 'Open this learning item for the full course material and supporting document.'}
                </p>

                <div className="mt-auto flex flex-wrap items-center gap-3 pt-5">
                    {course.document_url && (
                        <a
                            href={course.document_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex h-10 items-center justify-center rounded-md bg-[#16a34a] px-4 text-sm font-semibold text-white transition hover:bg-[#15803d]"
                        >
                            Open document
                        </a>
                    )}
                    {!course.document_url && (
                        <span className="inline-flex h-10 items-center justify-center rounded-md border border-slate-200 px-4 text-sm font-semibold text-slate-500">
                            Material pending
                        </span>
                    )}
                </div>
            </div>
        </article>
    );
};

export const Learning = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getList('public/learning', { pagination: { page: 1, perPage: 50 } })
            .then((response) => {
                setCourses(response.data || []);
                setLoading(false);
            })
            .catch(() => {
                toast.error('Error while loading learning courses', {});
                setLoading(false);
            });
    }, []);

    return (
        <div className="space-y-6">
            <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Learning library</p>
                <h1 className="mt-1 text-3xl font-semibold text-slate-950">Learning</h1>
            </div>

            <Panel className="border border-slate-200 shadow-sm">
                {loading && (
                    <div className="grid max-h-[calc(100vh-260px)] gap-5 overflow-y-auto pr-1 lg:grid-cols-2">
                        {Array.from({ length: 4 }, (_, index) => (
                            <div key={index} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                                <Skeleton variant="rectangular" width="100%" height={176} sx={{ bgcolor: 'grey.50' }} />
                                <div className="space-y-3 p-5">
                                    <Skeleton variant="text" width="30%" />
                                    <Skeleton variant="text" width="80%" />
                                    <Skeleton variant="text" width="65%" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && courses.length > 0 && (
                    <div className="grid max-h-[calc(100vh-260px)] gap-5 overflow-y-auto pr-1 lg:grid-cols-2">
                        {courses.map((course, index) => (
                            <CourseCard key={course.id || course.uid || index} course={course} index={index} />
                        ))}
                    </div>
                )}

                {!loading && courses.length === 0 && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
                        <h2 className="text-lg font-semibold text-slate-950">No learning courses yet</h2>
                        <p className="mt-2 text-sm text-slate-600">
                            Published courses from the admin content area will appear here.
                        </p>
                    </div>
                )}
            </Panel>
        </div>
    );
};
